import { type ChainFullId } from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import { useSelectedNetwork } from "@umami/state";
import { useCustomToast } from "@umami/utils";
import { useForm } from "react-hook-form";

import { useSubmitToRelayer } from "./useSubmitToRelayer";
import { SuccessStep } from "../../SendFlow/SuccessStep";

type UsePreviewShieldedTransactionProps = {
  chainId: ChainFullId;
  transaction: string | unknown;
};

export const usePreviewShieldedTransaction = ({
  chainId,
  transaction,
}: UsePreviewShieldedTransactionProps) => {
  const { openWith } = useDynamicModalContext();
  const network = useSelectedNetwork();
  const form = useForm();
  const toast = useCustomToast();

  // Only mainnet has a live shielded-pool contract; shadownet is wired but its
  // contract slot is still empty (see NETWORK_CONFIG in sapling-service-backend).
  // Networks without a saplingContractAddress (michelson / michelson-shadownet /
  // previewnet) never reach this hook — the UI gates the send-shielded flow on
  // that field — so map explicitly and fall back to `null` if a caller ever
  // slips through, forcing the submit to no-op instead of silently misrouting.
  const networkName: "mainnet" | "shadownet" | null =
    network.name === "mainnet" ? "mainnet" : network.name === "shadownet" ? "shadownet" : null;

  // Serialize transaction to string if it's an object
  const transactionString =
    typeof transaction === "string" ? transaction : JSON.stringify(transaction);

  const { submitToRelayer, isLoading } = useSubmitToRelayer({
    transaction: transactionString,
    network: networkName,
  });

  const handleSubmit = async () => {
    const result = await submitToRelayer();
    const hash = result?.operationHash;

    if (!hash) {
      toast({
        description: "Error submitting transaction, please try again",
        status: "info",
      });

      return;
    }

    await openWith(<SuccessStep chainId={chainId} hash={hash} />);
  };

  return {
    form,
    handleSubmit: form.handleSubmit(handleSubmit),
    isLoading,
  };
};
